/**
 * `<InterviewAnalysisTab>` — PER-ROUND interview analysis (per-round arc,
 * backend migration 0124 `interview_analyses.round_id`).
 *
 * The tab renders ONE `<RoundAnalysisSlot>` per JD interview round
 * (`jd.interview_rounds`, in order), plus a progress read ("2 of 3 rounds
 * analyzed"). Each slot is the pre-existing dropzone / paste / Teams-import →
 * generating → analysis state machine, but keyed by `round_id` so every slot
 * reads, polls, and mutates independently (see `<RoundAnalysisSlot>`).
 *
 * Fallbacks so nothing is hidden and behaviour never regresses below today:
 *   - Legacy analyses with `round_id = null` surface as an extra "Unassigned"
 *     slot (detected via a round-scoped `round_id IS NULL` read).
 *   - A JD with NO rounds falls back to a single default slot (`round_id=null`)
 *     — identical reach to the pre-per-round single-dropzone tab — ABOVE which
 *     an explicit notice links to the JD editor. Migration 0123 added
 *     `jds.interview_rounds` as additive-nullable with no backfill, so every JD
 *     created before the feature shipped has zero rounds; the lone fallback slot
 *     on its own read as "the feature is broken" rather than "this job has no
 *     rounds configured yet". There is deliberately NO data backfill: nothing in
 *     the pre-feature schema records a job's interview rounds (tenant pipeline
 *     stages are tenant-wide and model the candidate-status ladder, not named
 *     rounds), so any backfill would be fabricated round names attributed to the
 *     customer. Telling them, with a one-click path to fix it, is the honest fix.
 *
 * Teams degrades per `useTeamsAvailability()`: connected → each slot offers the
 * existing picker (now importing with the slot's `round_id`); not-connected →
 * a muted "connect Teams" fallback. The completed state reuses the EXISTING
 * `<AnalysisLoadedView>` four-card stack behind a "Full analysis ›" expander.
 */
import { useCallback, useMemo, useState, type JSX } from "react";
import { Link } from "react-router-dom";

import type { Candidate } from "@/hooks/queries/candidates/useCandidates";
import type { TabKey } from "@/components/candidate-page/tabs";
import { useTenantBase } from "@/hooks/useTenantBase";

import {
  RoundAnalysisSlot,
  type SlotStatus,
} from "@/components/candidate-page/analysis/RoundAnalysisSlot";
import { useTenantPipelineStages } from "@/hooks/queries/candidates/useTenantPipelineStages";
import { useJd } from "@/hooks/queries/candidates/useJd";
import { useInterviewAnalysis } from "@/hooks/queries/interview-analysis/useInterviewAnalysis";
import { useTeamsAvailability } from "@/hooks/queries/interview-analysis/useTeamsTranscripts";

interface JdSummary {
  id: string;
  name: string;
}

interface InterviewAnalysisTabProps {
  candidate: Candidate;
  jdId: string | undefined;
  activeTab: TabKey;
  /** Kept for backward-compat with the page wiring. */
  onTabChange: (tab: TabKey) => void;
  /** JDs available for the per-slot generation-context selector. */
  jds?: JdSummary[];
}

interface SlotSpec {
  key: string;
  roundId: string | null;
  name: string;
  roundNumber: number | null;
}

export function InterviewAnalysisTab({
  candidate,
  jdId,
  activeTab,
  jds = [],
}: InterviewAnalysisTabProps): JSX.Element {
  const isActive = activeTab === "analysis";

  // Resolve the candidate's active (non-terminal) pipeline stage — it still
  // supplies `stage_id` for every round's writes.
  const stagesQ = useTenantPipelineStages();
  const activeStage = useMemo(() => {
    if (!stagesQ.data) return null;
    const slug = candidate.status;
    if (!slug) return null;
    const stage = stagesQ.data.find((s) => s.slug === slug);
    if (!stage || stage.is_terminal) return null;
    return stage;
  }, [stagesQ.data, candidate.status]);

  // The JD's ordered rounds drive the slots.
  const jdQ = useJd(isActive ? jdId : undefined);
  const rounds = useMemo(
    () => (jdQ.data?.interview_rounds ?? []).filter((r) => !!r.id),
    [jdQ.data],
  );

  // Detect a legacy / unassigned (`round_id IS NULL`) analysis so it is never
  // hidden once real rounds exist.
  const legacyQ = useInterviewAnalysis({
    candidateId: candidate.id,
    type: "post",
    jdId,
    roundScoped: true,
    roundId: null,
    enabled: isActive && !!jdId && rounds.length > 0,
  });
  const hasLegacyUnassigned = !!legacyQ.data;

  const teamsAvailabilityQ = useTeamsAvailability(isActive);
  const teamsAvailable = teamsAvailabilityQ.data?.available === true;

  const slots = useMemo<SlotSpec[]>(() => {
    if (rounds.length === 0) {
      // No rounds defined → a single default slot (round_id=null). Same reach
      // as the pre-per-round single-dropzone tab.
      return [{ key: "unassigned", roundId: null, name: "Interview", roundNumber: null }];
    }
    const roundSlots: SlotSpec[] = rounds.map((r, i) => ({
      key: r.id as string,
      roundId: r.id as string,
      name: r.name,
      roundNumber: i + 1,
    }));
    if (hasLegacyUnassigned) {
      roundSlots.push({
        key: "unassigned",
        roundId: null,
        name: "Unassigned transcript",
        roundNumber: null,
      });
    }
    return roundSlots;
  }, [rounds, hasLegacyUnassigned]);

  // Per-slot status, lifted for the "X of N rounds analyzed" progress read.
  const [statuses, setStatuses] = useState<Record<string, SlotStatus>>({});
  const onStatusChange = useCallback((roundKey: string, status: SlotStatus) => {
    setStatuses((prev) => (prev[roundKey] === status ? prev : { ...prev, [roundKey]: status }));
  }, []);

  const definedRoundCount = rounds.length;
  const analyzedRoundCount = rounds.reduce(
    (n, r) => (statuses[r.id as string] === "analyzed" ? n + 1 : n),
    0,
  );

  // Explain the single fallback slot rather than letting it masquerade as the
  // whole feature. Gated on a RESOLVED JD read so the notice can't flash while
  // the JD is still in flight, and on `jdId` because a candidate opened with no
  // job context has no job to configure.
  const base = useTenantBase();
  const showNoRoundsNotice =
    isActive && !!jdId && jdQ.isSuccess && rounds.length === 0;

  return (
    <div
      className="scroll-primary flex flex-1 flex-col gap-4 overflow-x-hidden overflow-y-auto px-content-px py-5"
      data-testid="interview-analysis-tab"
    >
      <header className="flex items-center justify-between gap-3">
        <h2 className="text-base font-semibold text-text-primary">
          Interview Analysis
        </h2>
        {definedRoundCount > 0 ? (
          <span
            className="text-sm text-text-secondary"
            data-testid="round-progress"
          >
            {analyzedRoundCount} of {definedRoundCount} rounds analyzed
          </span>
        ) : null}
      </header>

      {showNoRoundsNotice ? (
        <div
          className="rounded-[var(--radius)] border border-border-default bg-surface-subtle px-4 py-3 text-sm text-text-secondary"
          data-testid="no-rounds-notice"
        >
          <span className="font-medium text-text-primary">
            No interview rounds are set up for this job.
          </span>{" "}
          Add them and each round — phone screen, hiring manager, panel, final —
          gets its own transcript and its own analysis. Until then, everything
          below lands in a single combined interview.{" "}
          <Link
            to={`${base}/jobs/${jdId}/edit`}
            className="font-medium text-accent-secondary underline-offset-2 hover:underline"
            data-testid="no-rounds-setup-link"
          >
            Set up interview rounds
          </Link>
        </div>
      ) : null}

      <div className="flex flex-col gap-3">
        {slots.map((slot) => (
          <RoundAnalysisSlot
            key={slot.key}
            candidate={candidate}
            jdId={jdId}
            activeStage={activeStage}
            roundId={slot.roundId}
            roundName={slot.name}
            roundNumber={slot.roundNumber}
            jds={jds}
            isActive={isActive}
            teamsAvailable={teamsAvailable}
            onStatusChange={onStatusChange}
          />
        ))}
      </div>
    </div>
  );
}
